import React, { useState, useEffect, useRef } from 'react';
import { Palette, Trash2, Pencil, X, Info, Settings } from 'lucide-react';
import Pagination from 'react-responsive-pagination';
import 'react-responsive-pagination/themes/classic.css';
import { uploadImage, getImages, deleteImage } from '../services/uploadService';
import PhotoGallery from './PhotoGallery';
import ConfirmDialog from './ConfirmDialog';
import ItemFormDialog from './ItemFormDialog';
import S3Image from './S3Image';
import ShimmerLoader from './ShimmerLoader';
import AddModelsModal from './AddModelsModal';
import AddUnitsModal from './AddUnitsModal';
import RichTextEditor from './RichTextEditor';
import ChangeHistoryButton from './ChangeHistoryButton';
import ChangeHistoryDrawer from './ChangeHistoryDrawer';
import { ColorCollectionModel } from '../types/colorCollectionModel';
import { ColorCollectionUnit } from '../types/colorCollectionUnit';
import { colorCollectionModelService } from '../services/colorCollectionModelService';
import { colorCollectionUnitService } from '../services/colorCollectionUnitService';
import api from '../services/api';
import '../styles/ColorCollectionDetailsSlider.css';

interface ColorCollectionDetailsSliderProps {
  workspaceId: string;
  colorCollectionId: string;
  onClose: () => void;
  onSave?: () => void;
}

interface Item {
  _id?: string;
  dsId?: string;
  name?: string;
  location?: string;
  type?: string;
  selection?: string;
  size?: string;
  finish?: string;
  modelNumber?: string;
  trade?: string;
  constructionNotes?: string;
  imageId?: string;
}

interface ItemCategory {
  name: string;
  items: Item[];
}

interface ColorCollection {
  _id: string;
  name: string;
  description?: string;
  constructionNotes?: string;
  projectId: string;
  projectName?: string;
  isActive: boolean;
  order: number;
  heroImageId?: string;
  photoIds?: string[];
  itemCategories?: ItemCategory[];
  createdAt: string;
  updatedAt: string;
}

interface Project {
  _id: string;
  name: string;
}

interface Package {
  _id: string;
  name: string;
  caption?: string;
  projectId: string;
  projectName?: string;
  isActive: boolean;
}

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface PurchasedUnit {
  unitNo: string;
  unitId: string;
  unitStatus: string;
  dealNumber: string;
  dealId: string;
  purchasers: Array<{ displayName: string; isPrimary: boolean; contactId: string }>;
  dealStage: string;
}

const ColorCollectionDetailsSlider: React.FC<ColorCollectionDetailsSliderProps> = ({
  workspaceId,
  colorCollectionId,
  onClose,
  onSave
}) => {
  const [colorCollection, setColorCollection] = useState<ColorCollection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);

  // Hero image
  const [uploadingHero, setUploadingHero] = useState(false);
  const [heroImageUrl, setHeroImageUrl] = useState<string | null>(null);
  const heroInputRef = useRef<HTMLInputElement>(null);

  // Basic info editing
  const [editingBasicInfo, setEditingBasicInfo] = useState(false);
  const [basicInfoData, setBasicInfoData] = useState({
    name: '',
    description: '',
    constructionNotes: '',
    projectId: '',
    isActive: true,
    order: 0
  });
  const [savingBasicInfo, setSavingBasicInfo] = useState(false);

  // Items and categories
  const [selectedCategory, setSelectedCategory] = useState<number>(0);
  const [editingCategory, setEditingCategory] = useState<number | null>(null);
  const [categoryName, setCategoryName] = useState('');
  const [savingCategories, setSavingCategories] = useState(false);
  const [draggedCategoryIndex, setDraggedCategoryIndex] = useState<number | null>(null);

  // Item dialog
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<{ item: Item | null; index: number | null }>({ item: null, index: null });
  const [savingItem, setSavingItem] = useState(false);

  // Models
  const [colorCollectionModels, setColorCollectionModels] = useState<ColorCollectionModel[]>([]);
  const [showAddModelsModal, setShowAddModelsModal] = useState(false);
  const [showEditModelsModal, setShowEditModelsModal] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [modelsSearchQuery, setModelsSearchQuery] = useState('');
  const [modelsPagination, setModelsPagination] = useState<PaginationData>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Units
  const [colorCollectionUnits, setColorCollectionUnits] = useState<ColorCollectionUnit[]>([]);
  const [showAddUnitsModal, setShowAddUnitsModal] = useState(false);
  const [showEditUnitsModal, setShowEditUnitsModal] = useState(false);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [unitsSearchQuery, setUnitsSearchQuery] = useState('');
  const [unitsPagination, setUnitsPagination] = useState<PaginationData>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Purchased Units
  const [purchasedUnits, setPurchasedUnits] = useState<PurchasedUnit[]>([]);
  const [loadingPurchasedUnits, setLoadingPurchasedUnits] = useState(false);
  const [purchasedUnitsPagination, setPurchasedUnitsPagination] = useState<PaginationData>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Tabs
  const [activeTab, setActiveTab] = useState<'basic' | 'models' | 'units' | 'purchased'>('basic');

  // Change history drawer
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  useEffect(() => {
    fetchProjects();
    fetchColorCollection();
    fetchPackages();
    // Clear stale data when color collection changes
    setColorCollectionModels([]);
    setColorCollectionUnits([]);
  }, [colorCollectionId]);

  useEffect(() => {
    // Disable body scroll when modal is open
    document.body.style.overflow = 'hidden';

    return () => {
      // Re-enable body scroll when modal closes
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    if (activeTab === 'models') {
      fetchColorCollectionModels();
    } else if (activeTab === 'units') {
      fetchColorCollectionUnits();
    } else if (activeTab === 'purchased') {
      fetchPurchasedUnits();
    }
  }, [activeTab, colorCollectionId, modelsPagination.page, modelsPagination.limit, modelsSearchQuery, unitsPagination.page, unitsPagination.limit, unitsSearchQuery, purchasedUnitsPagination.page, purchasedUnitsPagination.limit]);

  const fetchProjects = async () => {
    try {
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        params: { limit: 1000, sortBy: 'name', sortOrder: 'asc' }
      });
      setProjects(response.data.projects);
    } catch (err: any) {
      console.error('Error fetching projects:', err);
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await api.get(`/workspaces/${workspaceId}/pkg-packages`, {
        params: { limit: 1000 }
      });

      // Filter packages that have this color collection
      const filteredPackages = response.data.packages.filter((pkg: any) =>
        pkg.collections && pkg.collections.some((c: any) => c.collectionId === colorCollectionId)
      );
      setPackages(filteredPackages);
    } catch (err: any) {
      console.error('Error fetching packages:', err);
    }
  };

  const fetchColorCollection = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `/workspaces/${workspaceId}/pkg-color-collections/${colorCollectionId}`,
      );
      const data = response.data.colorCollection;
      setColorCollection(data);
      setBasicInfoData({
        name: data.name || '',
        description: data.description || '',
        constructionNotes: data.constructionNotes || '',
        projectId: data.projectId || '',
        isActive: data.isActive ?? true,
        order: data.order || 0
      });

      // Fetch hero image if exists
      if (data.heroImageId) {
        fetchHeroImage(data.heroImageId);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch color collection');
      console.error('Error fetching color collection:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHeroImage = async (imageId: string) => {
    try {
      const images = await getImages(workspaceId, {
        entityType: 'color_collection',
        entityId: colorCollectionId,
        imageType: 'hero'
      });
      if (images.length > 0) {
        setHeroImageUrl(images[0].url);
      }
    } catch (err) {
      console.error('Error fetching hero image:', err);
    }
  };

  const handleBasicInfoChange = (field: string, value: any) => {
    setBasicInfoData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveBasicInfo = async () => {
    try {
      setSavingBasicInfo(true);

      // Update existing
      await api.put(
        `/workspaces/${workspaceId}/pkg-color-collections/${colorCollectionId}`,
        basicInfoData,
      );

      setEditingBasicInfo(false);
      await fetchColorCollection();
      // Don't call onSave() here - it should only be called when closing the slider
      // The parent component has onSave={handleCloseSlider} which would close this view
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save color collection');
      console.error('Error saving color collection:', err);
    } finally {
      setSavingBasicInfo(false);
    }
  };

  const handleHeroImageClick = () => {
    heroInputRef.current?.click();
  };

  const handleHeroImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;

    const file = e.target.files[0];
    try {
      setUploadingHero(true);
      const uploaded = await uploadImage({
        workspaceId,
        file,
        entityType: 'color_collection',
        entityId: colorCollectionId,
        imageType: 'hero'
      });
      setHeroImageUrl(uploaded.url);

      // Update the colorCollection with heroImageId
      await api.put(
        `/workspaces/${workspaceId}/pkg-color-collections/${colorCollectionId}`,
        { heroImageId: uploaded._id },
      );
    } catch (err) {
      console.error('Error uploading hero image:', err);
    } finally {
      setUploadingHero(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  // Category handlers
  const handleAddCategory = () => {
    const newIndex = colorCollection?.itemCategories?.length || 0;
    setEditingCategory(newIndex);
    setCategoryName('');
  };

  const handleSaveCategory = async () => {
    if (!categoryName.trim()) return;

    try {
      setSavingCategories(true);

      const updatedCategories = [...(colorCollection?.itemCategories || [])];

      if (editingCategory !== null && editingCategory < updatedCategories.length) {
        // Edit existing category
        updatedCategories[editingCategory] = {
          ...updatedCategories[editingCategory],
          name: categoryName
        };
      } else {
        // Add new category
        updatedCategories.push({
          name: categoryName,
          items: []
        });
      }

      await api.put(
        `/workspaces/${workspaceId}/pkg-color-collections/${colorCollectionId}`,
        { itemCategories: updatedCategories },
      );

      // Update local state directly instead of fetching
      setColorCollection(prev => prev ? { ...prev, itemCategories: updatedCategories } : null);
      setEditingCategory(null);
      setCategoryName('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save category');
      console.error('Error saving category:', err);
    } finally {
      setSavingCategories(false);
    }
  };

  const handleCancelCategory = () => {
    setEditingCategory(null);
    setCategoryName('');
  };

  const handleEditCategory = (index: number) => {
    setEditingCategory(index);
    setCategoryName(colorCollection?.itemCategories?.[index]?.name || '');
  };

  const handleDeleteCategory = (index: number) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Category',
      message: 'Are you sure you want to delete this category and all its items?',
      onConfirm: async () => {
        try {
          setSavingCategories(true);
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));

          const updatedCategories = colorCollection?.itemCategories?.filter((_, i) => i !== index) || [];

          await api.put(
            `/workspaces/${workspaceId}/pkg-color-collections/${colorCollectionId}`,
            { itemCategories: updatedCategories },
          );

          if (selectedCategory >= updatedCategories.length) {
            setSelectedCategory(Math.max(0, updatedCategories.length - 1));
          }

          // Update local state directly instead of fetching
          setColorCollection(prev => prev ? { ...prev, itemCategories: updatedCategories } : null);
        } catch (err: any) {
          setError(err.response?.data?.message || 'Failed to delete category');
          console.error('Error deleting category:', err);
        } finally {
          setSavingCategories(false);
        }
      }
    });
  };

  // Drag and drop handlers for category reordering
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedCategoryIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();

    if (draggedCategoryIndex === null || draggedCategoryIndex === targetIndex) {
      setDraggedCategoryIndex(null);
      return;
    }

    try {
      const categories = [...(colorCollection?.itemCategories || [])];

      // Remove the dragged item
      const [draggedItem] = categories.splice(draggedCategoryIndex, 1);

      // Insert at new position
      categories.splice(targetIndex, 0, draggedItem);

      await api.put(
        `/workspaces/${workspaceId}/pkg-color-collections/${colorCollectionId}`,
        { itemCategories: categories },
      );

      // Update selected category if needed
      if (selectedCategory === draggedCategoryIndex) {
        setSelectedCategory(targetIndex);
      } else if (selectedCategory === targetIndex) {
        setSelectedCategory(draggedCategoryIndex < targetIndex ? targetIndex - 1 : targetIndex + 1);
      }

      // Update local state directly instead of fetching
      setColorCollection(prev => prev ? { ...prev, itemCategories: categories } : null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reorder categories');
      console.error('Error reordering categories:', err);
    } finally {
      setDraggedCategoryIndex(null);
    }
  };

  const handleDragEnd = () => {
    setDraggedCategoryIndex(null);
  };

  // Item handlers
  const handleAddItem = () => {
    setEditingItem({ item: null, index: null });
    setShowItemDialog(true);
  };

  const handleEditItem = (item: Item, index: number) => {
    setEditingItem({ item, index });
    setShowItemDialog(true);
  };

  const handleSaveItem = async (itemData: any) => {
    try {
      setSavingItem(true);
      const updatedCategories = [...(colorCollection?.itemCategories || [])];

      // Handle image upload if provided
      let imageId = itemData.imageId;

      if (itemData.imageFile) {
        const uploadedImage = await uploadImage({
          workspaceId,
          file: itemData.imageFile,
          entityType: 'color_collection',
          entityId: colorCollectionId,
          imageType: 'other'
        });
        imageId = uploadedImage._id;
      }

      // Remove imageFile and imageUrl from itemData before saving (we only save imageId)
      const { imageFile, imageUrl, ...itemDataToSave } = itemData;

      let finalItemData;
      if (editingItem.index !== null) {
        // Edit existing item - preserve all existing fields and update with new data
        const existingItem = updatedCategories[selectedCategory].items[editingItem.index];
        finalItemData = {
          ...existingItem, // Keep all existing fields (including _id, dsId, etc.)
          ...itemDataToSave, // Overwrite with new data from form
          imageId // Set the imageId
        };
        updatedCategories[selectedCategory].items[editingItem.index] = finalItemData;
      } else {
        // Add new item - no _id or dsId needed, MongoDB will generate them
        finalItemData = {
          ...itemDataToSave,
          imageId
        };
        updatedCategories[selectedCategory].items.push(finalItemData);
      }

      await api.put(
        `/workspaces/${workspaceId}/pkg-color-collections/${colorCollectionId}`,
        { itemCategories: updatedCategories },
      );

      setShowItemDialog(false);
      setEditingItem({ item: null, index: null });
      // Update local state directly instead of fetching
      setColorCollection(prev => prev ? { ...prev, itemCategories: updatedCategories } : null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save item');
      console.error('Error saving item:', err);
    } finally {
      setSavingItem(false);
    }
  };

  const handleDeleteItem = (itemIndex: number) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Item',
      message: 'Are you sure you want to delete this item?',
      onConfirm: async () => {
        try {
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));

          const updatedCategories = [...(colorCollection?.itemCategories || [])];
          updatedCategories[selectedCategory].items = updatedCategories[selectedCategory].items.filter(
            (_, i) => i !== itemIndex
          );

          await api.put(
            `/workspaces/${workspaceId}/pkg-color-collections/${colorCollectionId}`,
            { itemCategories: updatedCategories },
          );

          // Update local state directly instead of fetching
          setColorCollection(prev => prev ? { ...prev, itemCategories: updatedCategories } : null);
        } catch (err: any) {
          setError(err.response?.data?.message || 'Failed to delete item');
          console.error('Error deleting item:', err);
        }
      }
    });
  };

  // Model handlers
  const fetchColorCollectionModels = async () => {
    try {
      setLoadingModels(true);
      const response = await colorCollectionModelService.getByColorCollection(
        workspaceId,
        colorCollectionId,
        modelsPagination.page,
        modelsPagination.limit,
        modelsSearchQuery
      );
      setColorCollectionModels(response.models || []);

      // Update pagination if provided by the API
      if (response.pagination) {
        setModelsPagination(response.pagination);
      }
    } catch (err: any) {
      console.error('Error fetching color collection models:', err);
      setError(err.response?.data?.message || 'Failed to fetch models');
    } finally {
      setLoadingModels(false);
    }
  };

  const handleAddModels = async (models: Array<{ modelId: string; isUpgrade: boolean; cost: number; price: number }>) => {
    try {
      setLoadingModels(true);
      await colorCollectionModelService.createMultiple(
        workspaceId,
        colorCollectionId,
        models
      );
      await fetchColorCollectionModels();
      setShowAddModelsModal(false);
    } catch (err: any) {
      console.error('Error adding models:', err);
      setError(err.response?.data?.message || 'Failed to add models');
    } finally {
      setLoadingModels(false);
    }
  };

  const handleEditModels = async (models: Array<{ modelId: string; isUpgrade: boolean; cost: number; price: number }>) => {
    try {
      setLoadingModels(true);
      // Update all models in a single API call
      await colorCollectionModelService.updateMultiple(
        workspaceId,
        colorCollectionId,
        models
      );
      await fetchColorCollectionModels();
      setShowEditModelsModal(false);
    } catch (err: any) {
      console.error('Error updating models:', err);
      setError(err.response?.data?.message || 'Failed to update models');
    } finally {
      setLoadingModels(false);
    }
  };

  const handleDeleteModel = (modelId: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Remove Model',
      message: 'Are you sure you want to remove this model from the color collection?',
      onConfirm: async () => {
        try {
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
          await colorCollectionModelService.delete(workspaceId, colorCollectionId, modelId);
          await fetchColorCollectionModels();
        } catch (err: any) {
          console.error('Error deleting model:', err);
          setError(err.response?.data?.message || 'Failed to delete model');
        }
      }
    });
  };

  // Unit handlers
  const fetchColorCollectionUnits = async () => {
    try {
      setLoadingUnits(true);
      const response = await colorCollectionUnitService.getByColorCollection(
        workspaceId,
        colorCollectionId,
        unitsPagination.page,
        unitsPagination.limit,
        unitsSearchQuery
      );
      setColorCollectionUnits(response.units || []);

      // Update pagination if provided by the API
      if (response.pagination) {
        setUnitsPagination(response.pagination);
      }
    } catch (err: any) {
      console.error('Error fetching color collection units:', err);
      setError(err.response?.data?.message || 'Failed to fetch units');
    } finally {
      setLoadingUnits(false);
    }
  };

  const handleAddUnits = async (units: Array<{ unitId: string; isUpgrade: boolean; cost: number; price: number }>) => {
    try {
      setLoadingUnits(true);
      await colorCollectionUnitService.createMultiple(
        workspaceId,
        colorCollectionId,
        units
      );
      await fetchColorCollectionUnits();
      setShowAddUnitsModal(false);
    } catch (err: any) {
      console.error('Error adding units:', err);
      setError(err.response?.data?.message || 'Failed to add units');
    } finally {
      setLoadingUnits(false);
    }
  };

  const handleEditUnits = async (units: Array<{ unitId: string; isUpgrade: boolean; cost: number; price: number }>) => {
    try {
      setLoadingUnits(true);
      // Update all units in a single API call
      await colorCollectionUnitService.updateMultiple(
        workspaceId,
        colorCollectionId,
        units
      );
      await fetchColorCollectionUnits();
      setShowEditUnitsModal(false);
    } catch (err: any) {
      console.error('Error updating units:', err);
      setError(err.response?.data?.message || 'Failed to update units');
    } finally {
      setLoadingUnits(false);
    }
  };

  const handleDeleteUnit = (unitId: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Remove Unit',
      message: 'Are you sure you want to remove this unit from the color collection?',
      onConfirm: async () => {
        try {
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
          await colorCollectionUnitService.delete(workspaceId, colorCollectionId, unitId);
          await fetchColorCollectionUnits();
        } catch (err: any) {
          console.error('Error deleting unit:', err);
          setError(err.response?.data?.message || 'Failed to delete unit');
        }
      }
    });
  };

  // Pagination handlers for Models
  const handleModelsPageChange = (page: number) => {
    setModelsPagination({ ...modelsPagination, page });
  };

  const handleModelsLimitChange = (limit: number) => {
    setModelsPagination({ ...modelsPagination, limit, page: 1 });
  };

  const handleModelsSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setModelsSearchQuery(e.target.value);
    setModelsPagination({ ...modelsPagination, page: 1 });
  };

  // Purchased Units handlers
  const fetchPurchasedUnits = async () => {
    try {
      setLoadingPurchasedUnits(true);

      // Fetch purchased units from the API endpoint
      const response = await api.get(
        `/workspaces/${workspaceId}/pkg-color-collections/${colorCollectionId}/purchased-units`,
        {
          params: {
            page: purchasedUnitsPagination.page,
            limit: purchasedUnitsPagination.limit
          }
        }
      );

      setPurchasedUnits(response.data.purchasedUnits || []);
      if (response.data.pagination) {
        setPurchasedUnitsPagination(response.data.pagination);
      }
    } catch (err: any) {
      console.error('Error fetching purchased units:', err);
      setError(err.response?.data?.message || 'Failed to fetch purchased units');
    } finally {
      setLoadingPurchasedUnits(false);
    }
  };

  // Pagination handlers for Units
  const handleUnitsPageChange = (page: number) => {
    setUnitsPagination({ ...unitsPagination, page });
  };

  const handleUnitsLimitChange = (limit: number) => {
    setUnitsPagination({ ...unitsPagination, limit, page: 1 });
  };

  // Pagination handlers for Purchased Units
  const handlePurchasedUnitsPageChange = (page: number) => {
    setPurchasedUnitsPagination({ ...purchasedUnitsPagination, page });
  };

  const handlePurchasedUnitsLimitChange = (limit: number) => {
    setPurchasedUnitsPagination({ ...purchasedUnitsPagination, limit, page: 1 });
  };

  const handleUnitsSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUnitsSearchQuery(e.target.value);
    setUnitsPagination({ ...unitsPagination, page: 1 });
  };

  if (loading) {
    return (
      <div className="color-collection-details-slider">
        <div className="color-collection-details-header">
          <div className="color-collection-details-title">
            <h2>Loading...</h2>
          </div>
          <button className="color-collection-details-close-btn" onClick={handleClose}>
            <X size={24} />
          </button>
        </div>
        <div className="color-collection-details-content">
          <ShimmerLoader type="details" />
        </div>
      </div>
    );
  }

  return (
    <div className="color-collection-details-slider">
      {/* Header */}
      <div className="color-collection-details-header">
        <div className="color-collection-details-header-left">
          <button className="color-collection-details-back-btn" onClick={handleClose}>
            ← Back
          </button>
          <div className="color-collection-details-title">
            <h2>{colorCollectionId ? colorCollection?.name || 'Color Collection' : 'New Color Collection'}</h2>
            <p>{colorCollection?.projectName || 'Color Collection Details'}</p>
          </div>
        </div>
        <button className="color-collection-details-close-btn" onClick={handleClose}>
          <X size={24} />
        </button>
      </div>

      {/* Tabs */}
      <div className="color-collection-details-tabs">
        <button
          className={`color-collection-details-tab ${activeTab === 'basic' ? 'active' : ''}`}
          onClick={() => setActiveTab('basic')}
        >
          Basic Information
        </button>
        <button
          className={`color-collection-details-tab ${activeTab === 'models' ? 'active' : ''}`}
          onClick={() => setActiveTab('models')}
        >
          Models
        </button>
        <button
          className={`color-collection-details-tab ${activeTab === 'units' ? 'active' : ''}`}
          onClick={() => setActiveTab('units')}
        >
          Units
        </button>
        <button
          className={`color-collection-details-tab ${activeTab === 'purchased' ? 'active' : ''}`}
          onClick={() => setActiveTab('purchased')}
        >
          Purchased Units
        </button>
      </div>

      {/* Content */}
      <div className="color-collection-details-content">

            {/* Basic Information Tab */}
            {activeTab === 'basic' && (
              <>
              {/* First Row: Hero Image + Basic Information */}
              <div className="color-collection-details-grid">
                <div className="color-collection-details-left">
                  {/* Hero Photo Tile */}
                  <div className="color-collection-details-tile">
                    <div className="color-collection-details-tile-header">
                      <h3>Hero Image</h3>
                    </div>
                    <div className="color-collection-details-tile-content">
                    <div
                      onClick={handleHeroImageClick}
                      style={{
                        border: '2px dashed #D1D5DB',
                        borderRadius: '8px',
                        padding: '32px',
                        textAlign: 'center',
                        backgroundColor: '#F9FAFB',
                        cursor: 'pointer',
                        minHeight: '200px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {uploadingHero ? (
                        <div className="builders-loading">
                          <div className="builders-loading-spinner" style={{ width: '48px', height: '48px' }} />
                          <p style={{ marginTop: '16px', fontSize: '14px', color: '#6B7280' }}>Uploading...</p>
                        </div>
                      ) : heroImageUrl ? (
                        <img
                          src={heroImageUrl}
                          alt="Hero"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '200px',
                            objectFit: 'contain',
                            borderRadius: '4px'
                          }}
                        />
                      ) : (
                        <>
                          <Palette size={48} style={{ color: '#9CA3AF', marginBottom: '16px' }} />
                          <p style={{ fontSize: '14px', color: '#374151', marginBottom: '4px' }}>
                            Drag & drop image here
                          </p>
                          <p style={{ fontSize: '12px', color: '#6B7280' }}>
                            or click to browse
                          </p>
                        </>
                      )}
                    </div>
                    <input
                      ref={heroInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleHeroImageChange}
                      style={{ display: 'none' }}
                    />
                    </div>
                  </div>
                </div>

                <div className="color-collection-details-right">
                  {/* Basic Information Tile */}
                  <div className="color-collection-details-tile">
                    <div className="color-collection-details-tile-header">
                      <h3>Basic Information</h3>
                      {!editingBasicInfo && (
                        <button
                          className="color-collection-details-tile-edit"
                          onClick={() => setEditingBasicInfo(true)}
                        >
                          <Pencil size={16} />
                        </button>
                      )}
                    </div>
                    <div className="color-collection-details-tile-content">
                  {editingBasicInfo ? (
                    <div className="project-details-form">
                      {/* First Row: Name, Order, Status */}
                      <div className="form-row" style={{ display: 'flex', gap: '16px', flexWrap: 'nowrap', alignItems: 'flex-start' }}>
                        <div className="form-group" style={{ flex: '1', minWidth: '0' }}>
                          <label>Name *</label>
                          <input
                            type="text"
                            className="form-input"
                            value={basicInfoData.name}
                            onChange={(e) => handleBasicInfoChange('name', e.target.value)}
                            placeholder="Enter color collection name"
                          />
                        </div>

                        <div className="form-group" style={{ flex: '0 0 150px', minWidth: '150px' }}>
                          <label>Order</label>
                          <input
                            type="number"
                            className="form-input"
                            value={basicInfoData.order}
                            onChange={(e) => handleBasicInfoChange('order', parseInt(e.target.value) || 0)}
                          />
                        </div>

                        <div className="form-group" style={{ flex: '0 0 150px', minWidth: '150px' }}>
                          <label>Status</label>
                          <select
                            className="form-input"
                            value={basicInfoData.isActive ? 'active' : 'inactive'}
                            onChange={(e) => handleBasicInfoChange('isActive', e.target.value === 'active')}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </div>
                      </div>

                      {/* Second Row: Description (Full Width) */}
                      <div className="form-row" style={{ display: 'block', width: '100%', marginBottom: '20px' }}>
                        <div className="form-group" style={{ width: '100%' }}>
                          <label>Description</label>
                          <RichTextEditor
                            content={basicInfoData.description}
                            onChange={(content) => handleBasicInfoChange('description', content)}
                            placeholder="Enter description"
                          />
                        </div>
                      </div>

                      {/* Third Row: Construction Notes (Full Width) */}
                      <div className="form-row" style={{ display: 'block', width: '100%', marginBottom: '0' }}>
                        <div className="form-group" style={{ width: '100%' }}>
                          <label>Construction Notes</label>
                          <RichTextEditor
                            content={basicInfoData.constructionNotes}
                            onChange={(content) => handleBasicInfoChange('constructionNotes', content)}
                            placeholder="Enter construction notes"
                          />
                        </div>
                      </div>

                      <div className="color-collection-details-form-actions" style={{ marginTop: '20px' }}>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => {
                            setEditingBasicInfo(false);
                            // Reset to original data
                            if (colorCollection) {
                              setBasicInfoData({
                                name: colorCollection.name || '',
                                description: colorCollection.description || '',
                                constructionNotes: colorCollection.constructionNotes || '',
                                projectId: colorCollection.projectId || '',
                                isActive: colorCollection.isActive ?? true,
                                order: colorCollection.order || 0
                              });
                            }
                          }}
                          disabled={savingBasicInfo}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={handleSaveBasicInfo}
                          disabled={savingBasicInfo || !basicInfoData.name}
                        >
                          {savingBasicInfo ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      {/* First Row: Name, Order, Status */}
                      <div style={{ display: 'flex', gap: '20px', marginBottom: '16px' }}>
                        <div style={{ flex: '1' }}>
                          <div className="color-collection-details-info-row">
                            <span className="color-collection-details-info-label">Name:</span>
                            <span className="color-collection-details-info-value">{colorCollection?.name || '-'}</span>
                          </div>
                        </div>
                        <div style={{ flex: '0 0 150px' }}>
                          <div className="color-collection-details-info-row">
                            <span className="color-collection-details-info-label">Order:</span>
                            <span className="color-collection-details-info-value">{colorCollection?.order}</span>
                          </div>
                        </div>
                        <div style={{ flex: '0 0 150px' }}>
                          <div className="color-collection-details-info-row">
                            <span className="color-collection-details-info-label">Status:</span>
                            <span className="color-collection-details-info-value">
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                backgroundColor: colorCollection?.isActive ? '#DEF7EC' : '#FEE2E2',
                                color: colorCollection?.isActive ? '#03543F' : '#991B1B'
                              }}>
                                {colorCollection?.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Description (Full Width) */}
                      <div className="color-collection-details-info-row" style={{ marginBottom: '16px' }}>
                        <span className="color-collection-details-info-label">Description:</span>
                        <div
                          className="color-collection-details-info-value"
                          dangerouslySetInnerHTML={{ __html: colorCollection?.description || '-' }}
                        />
                      </div>

                      {/* Construction Notes (Full Width) */}
                      <div className="color-collection-details-info-row">
                        <span className="color-collection-details-info-label">Construction Notes:</span>
                        <div
                          className="color-collection-details-info-value"
                          dangerouslySetInnerHTML={{ __html: colorCollection?.constructionNotes || '-' }}
                        />
                      </div>
                    </div>
                  )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Items Tile */}
              <div className="color-collection-details-tile">
                <div className="color-collection-details-tile-header" style={{ flexDirection: 'column', alignItems: 'stretch', padding: '0' }}>
                  <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #E5E7EB' }}>
                    <h3 style={{ margin: 0 }}>Items</h3>
                  </div>

                  {/* Chrome-like Tabs */}
                  <div style={{
                    display: 'flex',
                    gap: '2px',
                    alignItems: 'flex-end',
                    padding: '0 12px',
                    backgroundColor: '#F3F4F6',
                    overflowX: 'auto',
                    minHeight: '42px'
                  }}>
                    {colorCollection?.itemCategories?.map((category, index) => (
                      <div
                        key={index}
                        draggable={editingCategory !== index}
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                        style={{ position: 'relative' }}
                      >
                        {editingCategory === index ? (
                          <div style={{
                            display: 'flex',
                            gap: '4px',
                            alignItems: 'center',
                            padding: '8px 12px',
                            background: 'white',
                            borderTopLeftRadius: '8px',
                            borderTopRightRadius: '8px',
                            boxShadow: selectedCategory === index ? '0 -2px 4px rgba(0,0,0,0.1)' : 'none'
                          }}>
                            <input
                              type="text"
                              value={categoryName}
                              onChange={(e) => setCategoryName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveCategory();
                                if (e.key === 'Escape') handleCancelCategory();
                              }}
                              placeholder="Category name"
                              autoFocus
                              style={{
                                padding: '4px 8px',
                                fontSize: '13px',
                                border: '1px solid #48182F',
                                borderRadius: '4px',
                                outline: 'none',
                                width: '120px'
                              }}
                            />
                            <button
                              onClick={handleSaveCategory}
                              disabled={savingCategories || !categoryName.trim()}
                              style={{
                                padding: '2px 6px',
                                fontSize: '12px',
                                background: '#48182F',
                                color: 'white',
                                border: 'none',
                                borderRadius: '3px',
                                cursor: 'pointer',
                                minWidth: '24px'
                              }}
                            >
                              ✓
                            </button>
                            <button
                              onClick={handleCancelCategory}
                              disabled={savingCategories}
                              style={{
                                padding: '2px 6px',
                                fontSize: '12px',
                                background: '#D1D5DB',
                                color: '#374151',
                                border: 'none',
                                borderRadius: '3px',
                                cursor: 'pointer',
                                minWidth: '24px'
                              }}
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <div
                            onClick={() => setSelectedCategory(index)}
                            onDoubleClick={() => handleEditCategory(index)}
                            style={{
                              padding: '8px 16px',
                              fontSize: '13px',
                              fontWeight: selectedCategory === index ? '600' : '500',
                              background: selectedCategory === index ? 'white' : '#E5E7EB',
                              color: selectedCategory === index ? '#48182F' : '#6B7280',
                              border: 'none',
                              borderTopLeftRadius: '8px',
                              borderTopRightRadius: '8px',
                              cursor: draggedCategoryIndex === index ? 'grabbing' : 'grab',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              minWidth: '100px',
                              position: 'relative',
                              boxShadow: selectedCategory === index ? '0 -2px 4px rgba(0,0,0,0.1)' : 'none',
                              zIndex: selectedCategory === index ? 2 : 1,
                              transition: 'all 0.2s ease',
                              opacity: draggedCategoryIndex === index ? 0.5 : 1
                            }}
                          >
                            <span style={{ flex: 1, whiteSpace: 'nowrap' }}>{category.name}</span>
                            {selectedCategory === index && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteCategory(index);
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#6B7280',
                                  cursor: 'pointer',
                                  padding: '0 4px',
                                  fontSize: '16px',
                                  lineHeight: '1',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '20px',
                                  height: '20px',
                                  borderRadius: '4px',
                                  transition: 'all 0.15s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = '#FEE2E2';
                                  e.currentTarget.style.color = '#DC2626';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = 'none';
                                  e.currentTarget.style.color = '#6B7280';
                                }}
                                title="Delete category"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Add Category Button */}
                    {editingCategory === (colorCollection?.itemCategories?.length || 0) ? (
                      <div style={{
                        display: 'flex',
                        gap: '4px',
                        alignItems: 'center',
                        padding: '8px 12px',
                        background: 'white',
                        borderTopLeftRadius: '8px',
                        borderTopRightRadius: '8px',
                        boxShadow: '0 -2px 4px rgba(0,0,0,0.1)'
                      }}>
                        <input
                          type="text"
                          value={categoryName}
                          onChange={(e) => setCategoryName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveCategory();
                            if (e.key === 'Escape') handleCancelCategory();
                          }}
                          placeholder="Category name"
                          autoFocus
                          style={{
                            padding: '4px 8px',
                            fontSize: '13px',
                            border: '1px solid #48182F',
                            borderRadius: '4px',
                            outline: 'none',
                            width: '120px'
                          }}
                        />
                        <button
                          onClick={handleSaveCategory}
                          disabled={savingCategories || !categoryName.trim()}
                          style={{
                            padding: '2px 6px',
                            fontSize: '12px',
                            background: '#48182F',
                            color: 'white',
                            border: 'none',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            minWidth: '24px'
                          }}
                        >
                          ✓
                        </button>
                        <button
                          onClick={handleCancelCategory}
                          disabled={savingCategories}
                          style={{
                            padding: '2px 6px',
                            fontSize: '12px',
                            background: '#D1D5DB',
                            color: '#374151',
                            border: 'none',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            minWidth: '24px'
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={handleAddCategory}
                        disabled={savingCategories}
                        style={{
                          padding: '8px 16px',
                          fontSize: '18px',
                          fontWeight: '500',
                          background: '#E5E7EB',
                          color: '#6B7280',
                          border: 'none',
                          borderTopLeftRadius: '8px',
                          borderTopRightRadius: '8px',
                          cursor: 'pointer',
                          minWidth: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#D1D5DB';
                          e.currentTarget.style.color = '#374151';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#E5E7EB';
                          e.currentTarget.style.color = '#6B7280';
                        }}
                        title="Add new category"
                      >
                        +
                      </button>
                    )}
                  </div>
                </div>

                <div className="color-collection-details-tile-content">
                  {!colorCollection?.itemCategories || colorCollection.itemCategories.length === 0 ? (
                    <p style={{ color: '#6B7280', textAlign: 'center', padding: '40px' }}>
                      No categories yet. Click "+ Add" to create your first category.
                    </p>
                  ) : (
                    <>
                      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                        <button
                          onClick={handleAddItem}
                          className="btn btn-primary btn-sm"
                        >
                          + Add Item
                        </button>
                      </div>
                      <div className="data-table-container">
                        <table className="data-table">
                        <thead>
                          <tr>
                            <th style={{ width: '60px' }}>Image</th>
                            <th>Name</th>
                            <th>Location</th>
                            <th>Type</th>
                            <th>Selection</th>
                            <th>Size</th>
                            <th>Finish</th>
                            <th>Model Number</th>
                            <th style={{ minWidth: '150px' }}>Trade</th>
                            <th style={{ width: '80px', textAlign: 'center' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {colorCollection.itemCategories[selectedCategory]?.items?.length === 0 ? (
                            <tr>
                              <td colSpan={10} className="data-table-empty">
                                No items in this category yet.
                              </td>
                            </tr>
                          ) : (
                            colorCollection.itemCategories[selectedCategory]?.items?.map((item, itemIndex) => (
                              <tr key={itemIndex}>
                                <td>
                                  {item.imageId ? (
                                    <S3Image
                                      workspaceId={workspaceId}
                                      imageId={item.imageId}
                                      alt={item.name || 'Item'}
                                      style={{
                                        width: '40px',
                                        height: '40px',
                                        objectFit: 'cover',
                                        borderRadius: '4px',
                                        border: '1px solid #E5E7EB'
                                      }}
                                    />
                                  ) : (
                                    <div style={{
                                      width: '40px',
                                      height: '40px',
                                      backgroundColor: '#E5E7EB',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      borderRadius: '4px'
                                    }}>
                                      <Settings size={20} color="#6B7280" />
                                    </div>
                                  )}
                                </td>
                                <td>{item.name || '-'}</td>
                                <td>{item.location || '-'}</td>
                                <td>{item.type || '-'}</td>
                                <td>{item.selection || '-'}</td>
                                <td>{item.size || '-'}</td>
                                <td>{item.finish || '-'}</td>
                                <td>{item.modelNumber || '-'}</td>
                                <td>{item.trade || '-'}</td>
                                <td>
                                  <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                    <button
                                      className="btn btn-sm btn-ghost"
                                      style={{ padding: '4px 8px', minWidth: '32px' }}
                                      onClick={() => handleEditItem(item, itemIndex)}
                                      title="Edit"
                                    >
                                      <Pencil size={16} />
                                    </button>
                                    <button
                                      className="btn btn-sm btn-ghost"
                                      style={{ padding: '4px 8px', minWidth: '32px', color: '#DC2626' }}
                                      onClick={() => handleDeleteItem(itemIndex)}
                                      title="Delete"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                    </>
                  )}
                </div>
              </div>

              {/* Packages Tile - Full Width */}
              <div className="color-collection-details-tile">
                <div className="color-collection-details-tile-header">
                  <h3>Packages</h3>
                </div>
                <div className="color-collection-details-tile-content">
                  {packages.length === 0 ? (
                    <p style={{ color: '#6B7280', textAlign: 'center', padding: '40px 20px' }}>
                      No packages are using this color collection
                    </p>
                  ) : (
                    <div className="data-table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Active</th>
                          </tr>
                        </thead>
                        <tbody>
                          {packages.map((pkg, index) => (
                            <tr key={pkg._id}>
                              <td className="data-table-primary-cell">{pkg.name}</td>
                              <td>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                  <input
                                    type="checkbox"
                                    checked={pkg.isActive}
                                    readOnly
                                    style={{ cursor: 'pointer' }}
                                  />
                                </label>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              {/* Photo Gallery Section - Full Width */}
              <div className="color-collection-details-tile">
                <div className="color-collection-details-tile-header">
                  <h3>Photo Gallery</h3>
                </div>
                <div className="color-collection-details-tile-content">
                  <PhotoGallery
                    workspaceId={workspaceId}
                    entityType="color_collection"
                    entityId={colorCollectionId}
                    category="gallery"
                    allowMultiple={true}
                  />
                </div>
              </div>
              </>
            )}

            {/* Models Tab */}
            {activeTab === 'models' && (
              <div className="color-collection-details-tile">
                  <div className="color-collection-details-tile-header">
                    <h3>Models</h3>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setShowEditModelsModal(true)}
                        disabled={colorCollectionModels.length === 0}
                      >
                        <Pencil size={16} style={{ marginRight: '4px' }} />
                        Edit Model
                      </button>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => setShowAddModelsModal(true)}
                      >
                        + Add Model
                      </button>
                    </div>
                  </div>
                  <div className="color-collection-details-tile-content">
                    {/* Search Box */}
                    <div style={{ marginBottom: '16px' }}>
                      <div className="contacts-search" style={{ position: 'relative', maxWidth: '400px' }}>
                        <input
                          type="text"
                          placeholder="Search models..."
                          value={modelsSearchQuery}
                          onChange={handleModelsSearchChange}
                          style={{ paddingRight: modelsSearchQuery ? '36px' : '16px' }}
                        />
                        {modelsSearchQuery && (
                          <button
                            onClick={() => {
                              setModelsSearchQuery('');
                              setModelsPagination({ ...modelsPagination, page: 1 });
                            }}
                            style={{
                              position: 'absolute',
                              right: '8px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#6B7280',
                              transition: 'color 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.color = '#374151'}
                            onMouseLeave={(e) => e.currentTarget.style.color = '#6B7280'}
                            aria-label="Clear search"
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="data-table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Model Collection</th>
                            <th>Model Name</th>
                            <th>Type</th>
                            <th>Upgrade</th>
                            <th>Cost</th>
                            <th>Price</th>
                            <th style={{ textAlign: 'right' }}>Info</th>
                            <th style={{ textAlign: 'right' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loadingModels ? (
                            <tr>
                              <td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                                Loading models...
                              </td>
                            </tr>
                          ) : colorCollectionModels.length === 0 ? (
                            <tr>
                              <td colSpan={8} className="data-table-empty">
                                {modelsSearchQuery ? 'No models found matching your search.' : 'No models added yet. Click "Add Model" to get started.'}
                              </td>
                            </tr>
                          ) : (
                            colorCollectionModels.map((model, index) => (
                              <tr key={model._id}>
                                <td>{model.modelCollection || '-'}</td>
                                <td>{model.modelName || '-'}</td>
                                <td>{model.modelType || '-'}</td>
                                <td>
                                  <span style={{
                                    padding: '4px 12px',
                                    borderRadius: '12px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    backgroundColor: model.isUpgrade ? '#DEF7EC' : '#FEE2E2',
                                    color: model.isUpgrade ? '#03543F' : '#991B1B'
                                  }}>
                                    {model.isUpgrade ? 'Upgrade' : 'Standard'}
                                  </span>
                                </td>
                                <td>${model.cost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td>${model.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td style={{ textAlign: 'right' }}>
                                  <button
                                    className="btn btn-sm btn-ghost"
                                    style={{
                                      background: '#000',
                                      color: '#fff',
                                      padding: '4px 8px',
                                      minWidth: '32px',
                                      marginLeft: 'auto'
                                    }}
                                    title="View model details"
                                  >
                                    <Info size={16} />
                                  </button>
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  <button
                                    className="btn btn-sm btn-ghost"
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      background: '#DC2626',
                                      color: '#fff',
                                      padding: '4px 8px',
                                      marginLeft: 'auto'
                                    }}
                                    onClick={() => handleDeleteModel(model.modelId)}
                                    title="Remove model"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination and Records Per Page */}
                    {!loadingModels && colorCollectionModels.length > 0 && (
                      <div className="contacts-pagination-controls" style={{ marginTop: '16px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 8px' }}>
                        {/* Records Per Page Selector */}
                        <div className="contacts-records-per-page">
                          <span>Show</span>
                          <select
                            value={modelsPagination.limit}
                            onChange={(e) => handleModelsLimitChange(Number(e.target.value))}
                          >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                          <span>records per page (Total: {modelsPagination.total})</span>
                        </div>

                        {/* Pagination */}
                        {modelsPagination.pages > 1 && (
                          <div className="contacts-pagination-wrapper">
                            <Pagination
                              current={modelsPagination.page}
                              total={modelsPagination.pages}
                              onPageChange={handleModelsPageChange}
                              maxWidth={400}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
              </div>
            )}

            {/* Units Tab */}
            {activeTab === 'units' && (
              <div className="color-collection-details-tile">
                  <div className="color-collection-details-tile-header">
                    <h3>Units</h3>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setShowEditUnitsModal(true)}
                        disabled={colorCollectionUnits.length === 0}
                      >
                        <Pencil size={16} style={{ marginRight: '4px' }} />
                        Edit Unit
                      </button>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => setShowAddUnitsModal(true)}
                      >
                        + Add Unit
                      </button>
                    </div>
                  </div>
                  <div className="color-collection-details-tile-content">
                    {/* Search Box */}
                    <div style={{ marginBottom: '16px' }}>
                      <div className="contacts-search" style={{ position: 'relative', maxWidth: '400px' }}>
                        <input
                          type="text"
                          placeholder="Search units..."
                          value={unitsSearchQuery}
                          onChange={handleUnitsSearchChange}
                          style={{ paddingRight: unitsSearchQuery ? '36px' : '16px' }}
                        />
                        {unitsSearchQuery && (
                          <button
                            onClick={() => {
                              setUnitsSearchQuery('');
                              setUnitsPagination({ ...unitsPagination, page: 1 });
                            }}
                            style={{
                              position: 'absolute',
                              right: '8px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#6B7280',
                              transition: 'color 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.color = '#374151'}
                            onMouseLeave={(e) => e.currentTarget.style.color = '#6B7280'}
                            aria-label="Clear search"
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="data-table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Unit No</th>
                            <th>Floor</th>
                            <th>Model</th>
                            <th>Status</th>
                            <th>Upgrade</th>
                            <th>Cost</th>
                            <th>Price</th>
                            <th style={{ textAlign: 'right' }}>Info</th>
                            <th style={{ textAlign: 'right' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loadingUnits ? (
                            <tr>
                              <td colSpan={9} style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                                Loading units...
                              </td>
                            </tr>
                          ) : colorCollectionUnits.length === 0 ? (
                            <tr>
                              <td colSpan={9} className="data-table-empty">
                                {unitsSearchQuery ? 'No units found matching your search.' : 'No units added yet. Click "Add Unit" to get started.'}
                              </td>
                            </tr>
                          ) : (
                            colorCollectionUnits.map((unit, index) => (
                                <tr key={unit._id}>
                                  <td>{unit.unitNo || '-'}</td>
                                  <td>{unit.floorMarketing || '-'}</td>
                                  <td>{unit.modelName || '-'}</td>
                                  <td>
                                    <span style={{
                                      padding: '4px 8px',
                                      borderRadius: '4px',
                                      fontSize: '11px',
                                      fontWeight: '500',
                                      backgroundColor:
                                        unit.status === 'AVAILABLE' ? '#DEF7EC' :
                                        unit.status === 'RESERVED' ? '#FEF3C7' :
                                        unit.status === 'SOLD' ? '#FEE2E2' : '#F3F4F6',
                                      color:
                                        unit.status === 'AVAILABLE' ? '#03543F' :
                                        unit.status === 'RESERVED' ? '#92400E' :
                                        unit.status === 'SOLD' ? '#991B1B' : '#6B7280'
                                    }}>
                                      {unit.status || '-'}
                                    </span>
                                  </td>
                                  <td>
                                    <span style={{
                                      padding: '4px 12px',
                                      borderRadius: '12px',
                                      fontSize: '12px',
                                      fontWeight: '500',
                                      backgroundColor: unit.isUpgrade ? '#DEF7EC' : '#FEE2E2',
                                      color: unit.isUpgrade ? '#03543F' : '#991B1B'
                                    }}>
                                      {unit.isUpgrade ? 'Upgrade' : 'Standard'}
                                    </span>
                                  </td>
                                  <td>${unit.cost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                  <td>${unit.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                  <td style={{ textAlign: 'right' }}>
                                    <button
                                      className="btn btn-sm btn-ghost"
                                      style={{
                                        background: '#000',
                                        color: '#fff',
                                        padding: '4px 8px',
                                        minWidth: '32px',
                                        marginLeft: 'auto'
                                      }}
                                      title="View unit details"
                                    >
                                      <Info size={16} />
                                    </button>
                                  </td>
                                  <td style={{ textAlign: 'right' }}>
                                    <button
                                      className="btn btn-sm btn-ghost"
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        background: '#DC2626',
                                        color: '#fff',
                                        padding: '4px 8px',
                                        marginLeft: 'auto'
                                      }}
                                      onClick={() => handleDeleteUnit(unit.unitId)}
                                      title="Remove unit"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  </td>
                                </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination and Records Per Page */}
                    {!loadingUnits && colorCollectionUnits.length > 0 && (
                      <div className="contacts-pagination-controls" style={{ marginTop: '16px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 8px' }}>
                        {/* Records Per Page Selector */}
                        <div className="contacts-records-per-page">
                          <span>Show</span>
                          <select
                            value={unitsPagination.limit}
                            onChange={(e) => handleUnitsLimitChange(Number(e.target.value))}
                          >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                          <span>records per page (Total: {unitsPagination.total})</span>
                        </div>

                        {/* Pagination */}
                        {unitsPagination.pages > 1 && (
                          <div className="contacts-pagination-wrapper">
                            <Pagination
                              current={unitsPagination.page}
                              total={unitsPagination.pages}
                              onPageChange={handleUnitsPageChange}
                              maxWidth={400}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
              </div>
            )}

            {/* Purchased Units Tab */}
            {activeTab === 'purchased' && (
              <div className="color-collection-details-tile">
                <div className="color-collection-details-tile-content">
                  <div className="data-table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Unit#</th>
                          <th>Unit Status</th>
                          <th>Deal#</th>
                          <th>Purchaser(s)</th>
                          <th>Deal Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingPurchasedUnits ? (
                          <tr>
                            <td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                              Loading purchased units...
                            </td>
                          </tr>
                        ) : purchasedUnits.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="data-table-empty">
                              No purchased units found for this color collection.
                            </td>
                          </tr>
                        ) : (
                          purchasedUnits.map((unit) => (
                            <tr key={unit.dealId}>
                              <td className="data-table-primary-cell">
                                <span
                                  onClick={() => window.open(`/workspaces/${workspaceId}/units?view=${unit.unitId}`, '_blank')}
                                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                  title="View unit details"
                                >
                                  {unit.unitNo}
                                </span>
                              </td>
                              <td>
                                <span style={{
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  backgroundColor: '#FEE2E2',
                                  color: '#991B1B'
                                }}>
                                  SOLD
                                </span>
                              </td>
                              <td>
                                <span
                                  onClick={() => window.open(`/workspaces/${workspaceId}/deals?view=${unit.dealId}`, '_blank')}
                                  style={{ cursor: 'pointer', textDecoration: 'underline', color: '#48182F' }}
                                  title="View deal details"
                                >
                                  {unit.dealNumber}
                                </span>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                {unit.purchasers && unit.purchasers.length > 0 ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                    {unit.purchasers.map((p, idx) => (
                                      <span
                                        key={idx}
                                        onClick={() => window.open(`/workspaces/${workspaceId}/contacts?view=${p.contactId}`, '_blank')}
                                        style={{
                                          fontWeight: p.isPrimary ? '600' : '400',
                                          cursor: 'pointer',
                                          textDecoration: 'underline'
                                        }}
                                        title="View purchaser details"
                                      >
                                        {p.displayName}
                                      </span>
                                    ))}
                                  </div>
                                ) : '-'}
                              </td>
                              <td>
                                <span style={{
                                  padding: '4px 12px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  backgroundColor:
                                    unit.dealStage === 'FIRM' ? '#DEF7EC' :
                                    unit.dealStage === 'RESERVED' ? '#FEF3C7' :
                                    unit.dealStage === 'UNIT RELEASED' ? '#DBEAFE' :
                                    unit.dealStage === 'SOLD' ? '#DEF7EC' : '#F3F4F6',
                                  color:
                                    unit.dealStage === 'FIRM' ? '#03543F' :
                                    unit.dealStage === 'RESERVED' ? '#92400E' :
                                    unit.dealStage === 'UNIT RELEASED' ? '#1E40AF' :
                                    unit.dealStage === 'SOLD' ? '#03543F' : '#6B7280'
                                }}>
                                  {unit.dealStage}
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination and Records Per Page */}
                  {!loadingPurchasedUnits && purchasedUnits.length > 0 && (
                    <div className="contacts-pagination-controls" style={{ marginTop: '16px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 8px' }}>
                      {/* Records Per Page Selector */}
                      <div className="contacts-records-per-page">
                        <span>Show</span>
                        <select
                          value={purchasedUnitsPagination.limit}
                          onChange={(e) => handlePurchasedUnitsLimitChange(Number(e.target.value))}
                        >
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                        <span>records per page (Total: {purchasedUnitsPagination.total})</span>
                      </div>

                      {/* Pagination */}
                      {purchasedUnitsPagination.pages > 1 && (
                        <div className="contacts-pagination-wrapper">
                          <Pagination
                            current={purchasedUnitsPagination.page}
                            total={purchasedUnitsPagination.pages}
                            onPageChange={handlePurchasedUnitsPageChange}
                            maxWidth={400}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

      </div>

      {/* Footer */}
      <div className="color-collection-details-footer">
        <button
          className="btn btn-secondary btn-sm"
          onClick={handleClose}
        >
          Close
        </button>
      </div>

      {/* Item Form Dialog */}
      {showItemDialog && (
          <ItemFormDialog
            item={editingItem.item}
            workspaceId={workspaceId}
            onClose={() => {
              setShowItemDialog(false);
              setEditingItem({ item: null, index: null });
            }}
            onSave={handleSaveItem}
          />
      )}

      {/* Add Models Modal */}
      {showAddModelsModal && (
        <AddModelsModal
            isOpen={showAddModelsModal}
            onClose={() => setShowAddModelsModal(false)}
            onSave={handleAddModels}
            workspaceId={workspaceId}
            projectId={colorCollection?.projectId || ''}
            existingModels={colorCollectionModels}
            editMode={false}
          />
      )}

      {/* Edit Models Modal */}
      {showEditModelsModal && (
        <AddModelsModal
            isOpen={showEditModelsModal}
            onClose={() => setShowEditModelsModal(false)}
            onSave={handleEditModels}
            workspaceId={workspaceId}
            projectId={colorCollection?.projectId || ''}
            existingModels={colorCollectionModels}
            editMode={true}
          />
      )}

      {/* Add Units Modal */}
      {showAddUnitsModal && (
        <AddUnitsModal
            isOpen={showAddUnitsModal}
            onClose={() => setShowAddUnitsModal(false)}
            onSave={handleAddUnits}
            workspaceId={workspaceId}
            projectId={colorCollection?.projectId || ''}
            existingUnits={colorCollectionUnits}
            editMode={false}
          />
      )}

      {/* Edit Units Modal */}
      {showEditUnitsModal && (
        <AddUnitsModal
            isOpen={showEditUnitsModal}
            onClose={() => setShowEditUnitsModal(false)}
            onSave={handleEditUnits}
            workspaceId={workspaceId}
            projectId={colorCollection?.projectId || ''}
            existingUnits={colorCollectionUnits}
            editMode={true}
          />
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonStyle="danger"
      />

      {/* Change History FAB Button */}
      <ChangeHistoryButton
        onClick={() => setHistoryDrawerOpen(true)}
        isOpen={historyDrawerOpen}
      />

      {/* Change History Drawer */}
      <ChangeHistoryDrawer
        isOpen={historyDrawerOpen}
        onClose={() => setHistoryDrawerOpen(false)}
        workspaceId={workspaceId}
        entityType="color_collection"
        entityId={colorCollectionId}
      />
    </div>
  );
};

export default ColorCollectionDetailsSlider;
